import React, { useEffect, useState } from 'react';
import { fetchRoles, createRole, deleteRole } from '../../services/api/adminRbacRoles';
import { fetchPermissions, savePermissions } from '../../services/api/adminRbacRolePermissions';
import { fetchFeatureCatalog } from '../../services/api/adminFeatureCatalog';
import RoleListSidebar from '../../components/admin/RoleListSidebar';
import PermissionTree from '../../components/admin/PermissionTree';
import CreateRoleModal from '../../components/admin/CreateRoleModal';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

const AdminRolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState({ features: [] });
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissions, setPermissions] = useState({});  // component_id => {can_read, can_write}
  const [pending, setPending] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const isDirty = Object.keys(pending).length > 0;
  useUnsavedChangesPrompt(isDirty);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [rolesRes, catRes] = await Promise.all([fetchRoles(), fetchFeatureCatalog()]);
        setRoles(rolesRes.data?.roles || []);
        setCatalog(catRes.data);
        if (rolesRes.data?.roles?.length) {
          setSelectedRole(rolesRes.data.roles[0]);
        }
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedRole) return;
    const load = async () => {
      try {
        const res = await fetchPermissions(selectedRole.id);
        const map = {};
        (res.data?.permissions || []).forEach((p) => {
          map[p.feature_component_id] = { can_read: p.can_read, can_write: p.can_write };
        });
        setPermissions(map);
        setPending({});
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      }
    };
    load();
  }, [selectedRole?.id]);

  const handleToggle = (componentId, field, value) => {
    if (selectedRole?.is_system) return;
    const current = pending[componentId] || permissions[componentId] || { can_read: false, can_write: false };
    const updated = { ...current, [field]: value };
    // If reverting to original DB value, drop from pending
    const original = permissions[componentId] || { can_read: false, can_write: false };
    if (updated.can_read === original.can_read && updated.can_write === original.can_write) {
      const { [componentId]: _, ...rest } = pending;
      setPending(rest);
    } else {
      setPending({ ...pending, [componentId]: updated });
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const changes = Object.entries(pending).map(([cid, p]) => ({
        feature_component_id: parseInt(cid, 10),
        can_read: p.can_read,
        can_write: p.can_write,
      }));
      await savePermissions(selectedRole.id, changes);
      setSuccess(`Saved ${changes.length} change${changes.length === 1 ? '' : 's'}.`);
      setPermissions({ ...permissions, ...pending });
      setPending({});
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (payload) => {
    const res = await createRole(payload);
    const created = res.data;
    const refreshed = await fetchRoles();
    setRoles(refreshed.data.roles || []);
    setSelectedRole(created);
    setShowCreate(false);
  };

  const handleDelete = async () => {
    if (!selectedRole || selectedRole.is_system) return;
    if (!window.confirm(`Delete role "${selectedRole.label}"?`)) return;
    try {
      await deleteRole(selectedRole.id);
      const refreshed = await fetchRoles();
      setRoles(refreshed.data.roles || []);
      setSelectedRole(refreshed.data.roles?.[0] || null);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  };

  if (loading) return <div className="p-4 text-sm text-muted-foreground">Loading...</div>;

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      <RoleListSidebar
        roles={roles}
        selectedRoleId={selectedRole?.id}
        onSelect={setSelectedRole}
        onCreate={() => setShowCreate(true)}
      />
      <div className="flex-1 overflow-y-auto p-4 pb-24">
        {selectedRole && (
          <>
            <div className="mb-6 flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  {selectedRole.label}
                  {selectedRole.is_system && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">SYSTEM ROLE — read-only</span>
                  )}
                </h1>
                {selectedRole.description && (
                  <p className="text-sm text-muted-foreground mt-1">{selectedRole.description}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {selectedRole.member_count || 0} member{selectedRole.member_count === 1 ? '' : 's'} assigned
                </p>
              </div>
              {!selectedRole.is_system && (
                <button
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:underline"
                >
                  Delete role
                </button>
              )}
            </div>

            {error && <div className="mb-4 p-3 bg-red-50 text-red-700 text-sm rounded-lg">{error}</div>}
            {success && <div className="mb-4 p-3 bg-green-50 text-green-700 text-sm rounded-lg">{success}</div>}

            <PermissionTree
              catalog={catalog}
              permissions={permissions}
              pending={pending}
              onToggle={handleToggle}
              readOnly={selectedRole.is_system}
            />
          </>
        )}
      </div>
      {isDirty && !selectedRole?.is_system && (
        <div className="fixed bottom-0 right-0 left-64 bg-card border-t border-border px-6 py-3 flex justify-end gap-2 shadow-lg">
          <span className="text-sm text-muted-foreground mr-auto self-center">
            {Object.keys(pending).length} pending change{Object.keys(pending).length === 1 ? '' : 's'}
          </span>
          <button
            onClick={() => setPending({})}
            disabled={saving}
            className="text-sm text-foreground border border-input rounded-lg px-4 py-2 hover:bg-muted/50"
          >
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-sm font-medium text-white bg-black rounded-lg px-4 py-2 hover:bg-gray-800"
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      )}
      {showCreate && (
        <CreateRoleModal
          existingRoles={roles}
          onClose={() => setShowCreate(false)}
          onCreate={handleCreate}
        />
      )}
    </div>
  );
};

export default AdminRolePermissions;
